import {InMemoryCache, IntrospectionFragmentMatcher} from 'apollo-cache-inmemory';
import {ApolloClient} from 'apollo-client';
import {ApolloLink} from 'apollo-link';
import {onError} from 'apollo-link-error';
import {HttpLink} from 'apollo-link-http';
import {toIdValue} from 'apollo-utilities';

import schema from '../../fragmentTypes.json';

// We get a 401 back from GraphQL when the user trying to make requests doesn't
// have a valid session and therefore is unauthorized to make requests
const RESPONSE_CODE_UNAUTHORIZED = 401;

// We get a 404 back when the user has _never_ established a valid session and therefore
// never got a valid GraphQL schema generated, and can't make requests
const RESPONSE_CODE_SESSION_NEVER_ESTABLISHED = 404;

const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: schema.data,
});

// This is a very simple caching translation function to store all of our
// items in the cache by their id.
//
// We'll use this elsewhere to actual resolve some items from the cache
// by a similar mechanism
// @ts-expect-error
const cacheNormalizeFunction = (object) => object.id;
// Cache resolvers act as ways to resolve certain queries so that the request
// can find existing data in the cache without having to hit the server.
//
// For example, if we request a list of step templates, we have a list of ids of those
// step templates in our cache. Down the road, if a different query requests just a
// single step template, it won't automatically find it in the cache (you'd think it
// should, but it just auto-resolves in the cache by-query).
//
// So, we'll set up resolvers for those queries to take its input and using
// our resolver function look up in the cache to see if that piece of data
// already exists.
const cacheResolvers = {
    // Query and Mutation are top-level keys built into the InMemoryCache's
    // cache resolver
    // hello world
    Query: {
        // `stepTemplate` is the name of the Nutshell-defined query we're going
        // to resolve
        //
        // `toIdValue` ensures an ID type is returned.
        // @ts-expect-error
        stepTemplate: (_, {id}) =>
            toIdValue(cacheNormalizeFunction({__typename: 'StepTemplate', id})),
        // @ts-expect-error
        closedLeadSlackMessage: (_, {id}) =>
            toIdValue(cacheNormalizeFunction({__typename: 'ClosedLeadSlackMessage', id})),
        // @ts-expect-error
        openLeadSlackMessage: (_, {id}) =>
            toIdValue(cacheNormalizeFunction({__typename: 'OpenLeadSlackMessage', id})),
        // @ts-expect-error
        stageset: (_, {id}) => toIdValue(cacheNormalizeFunction({__typename: 'Stage', id})),
        // @ts-expect-error
        stage: (_, {id}) => toIdValue(cacheNormalizeFunction({__typename: 'Stageset', id})),
        // We allow requesting a lead by number, and by id. We want to make sure that leads
        // already requested by number (and therefore exist in the cache) are used when
        // requesting the _same_ lead by id.
        //
        // @see: https://www.apollographql.com/docs/react/advanced/caching#cacheRedirect
        // @ts-expect-error
        lead: (_, variables, helpers) =>
            helpers.getCacheKey({__typename: 'Lead', id: variables.id}),
    },
} as const;

const cache = new InMemoryCache({
    // @ts-expect-error
    cacheRedirects: cacheResolvers,
    dataIdFromObject: cacheNormalizeFunction,
    fragmentMatcher: fragmentMatcher,
});

const httpLink = new HttpLink({
    uri: (operation) => `/graphql?${operation.operationName}`,
    credentials: 'same-origin',
    fetch: (uri, options) => {
        // Note: Duplicated in client/peanut/src/apollo-client.ts
        return fetch(uri, options).then((response) => {
            // We throw 500s on the backend for logging / debugging purposes,
            // but want our clients to treat them as 200s so they can parse
            // the errors properly. This is the normal behavior for GraphQL

            // Some reading: https://github.com/rmosolgo/graphql-ruby/issues/1130
            if (response.status === 500) {
                return response.text().then((body) => {
                    try {
                        const parsed = JSON.parse(body);
                        // If the response contains GraphQL errors, recast
                        // the response as a 200.
                        if (parsed.errors) {
                            return new Response(body, {
                                status: 200,
                                statusText: 'OK',
                                headers: response.headers,
                            });
                        } else {
                            return response;
                        }
                    } catch (_e: any) {
                        // Don’t care if parsing fails, just return response.
                    }

                    // If we got here, it means the 500 was not a
                    // GraphQL error, and we don't need to do anything
                    // in the client.
                    return response;
                });
            }

            return response;
        });
    },
});

const errorLink = onError(({operation, graphQLErrors, networkError}) => {
    const {response} = operation.getContext();

    if (response && response.status === RESPONSE_CODE_UNAUTHORIZED) {
        // @ts-expect-error
        if (typeof window.trackJs !== 'undefined') {
            // @ts-expect-error
            window.trackJs.console.info(
                'Unauthorized (401) GraphQL Request! Logging request and logging out user…',
                operation
            );
        }

        // Quick Fix: 2022-03-25
        // Expired trial on billing page is getting constant refresh due to this line.
        // window.location.href = '/auth?sessionTimeout=1';
    }

    if (response && response.status === RESPONSE_CODE_SESSION_NEVER_ESTABLISHED) {
        // @ts-expect-error
        if (typeof window.trackJs !== 'undefined') {
            // @ts-expect-error
            window.trackJs.console.info(
                'Unauthorized (404) GraphQL Request! Logging request and logging out user…',
                operation
            );
        }

        window.location.href = '/auth?sessionTimeout=1';
    }

    // @ts-expect-error
    if (typeof window.trackJs !== 'undefined') {
        // @ts-expect-error
        window.trackJs.console.info('GraphQL Error! Logging request…', operation);

        if (graphQLErrors) {
            graphQLErrors.forEach(({message, path}, index) => {
                // @ts-expect-error
                window.trackJs.console.info(
                    `Client error ${index}: Message: ${message}, Path: ${path}`
                );
            });
        }

        if (networkError) {
            // @ts-expect-error
            window.trackJs.console.info('Logging GraphQL server errors…');
            // @ts-expect-error
            window.trackJs.console.info('Network error:', networkError);
        }
    }
});

export const nutApolloClient = new ApolloClient({
    link: ApolloLink.from([errorLink, httpLink]),
    cache: cache,
    // Normally, this isn't necessary in development, but there's some
    // bug in the chrome extension that actually needs this to be `true` for
    // it to work anywhere.
    connectToDevTools: true,
});
